/**
 * The cases of each eval suite, with every version: a tree on the left (each case, and the
 * versions of the selected one under it), the selected version on the right. New runs use
 * the current version of each active case and save a copy, so a new version never changes
 * old runs. Cases and versions from Claude wait here until someone makes them current. The
 * suite, case and version are in the query string, so a view can be shared.
 */
import { ArrowLeft, Bot, ChevronDown, ChevronRight, Plus, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Button from '../../components/Button';
import ErrorDisplay from '../../components/ErrorDisplay';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useAuth } from '../../hooks/useAuth';
import { CaseEditor } from './components/cases/CaseEditor';
import { CasePanel } from './components/cases/CasePanel';
import { CaseStatusBadge } from './components/cases/CaseStatusBadge';
import { evalsService } from './services/evalsService';
import type { EvalCaseDetail, EvalCaseInfo, EvalCaseStatus, EvalCaseVersion, EvalSuiteInfo } from './types/evalTypes';
import { formatDateTime } from './utils/evalFormat';

type Filter = EvalCaseStatus | 'all';

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'proposed', label: 'Proposed' },
  { value: 'archived', label: 'Archived' },
];

const withoutVersions = ({ versions: _versions, ...info }: EvalCaseDetail): EvalCaseInfo => info;

export const Cases = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [suites, setSuites] = useState<EvalSuiteInfo[]>([]);
  const suiteIds = suites.map((suite) => suite.id);
  const [cases, setCases] = useState<EvalCaseInfo[]>([]);
  const [detail, setDetail] = useState<EvalCaseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingCase, setLoadingCase] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const suiteId = searchParams.get('suite') ?? suiteIds[0] ?? '';
  const caseId = searchParams.get('case');
  const creating = searchParams.get('new') === '1';
  const caseOptions = suites.find((suite) => suite.id === suiteId)?.caseOptions ?? {};

  const show = (params: { case?: string; version?: string; new?: '1' }) => setSearchParams({ suite: suiteId, ...params }, { replace: true });

  useEffect(() => {
    evalsService.listSuites().then(
      (catalog) => setSuites(catalog.suites),
      (caught: unknown) => setError(caught instanceof Error ? caught.message : 'Failed to load the suites.'),
    );
  }, []);

  const loadCases = useCallback(async () => {
    if (!suiteId) return;
    setLoading(true);
    setError(null);
    try {
      setCases(await evalsService.listCases(suiteId));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to load the cases.');
    } finally {
      setLoading(false);
    }
  }, [suiteId]);

  useEffect(() => {
    void loadCases();
  }, [loadCases]);

  useEffect(() => {
    if (!suiteId || !caseId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setLoadingCase(true);
    evalsService
      .getCase(suiteId, caseId)
      .then(
        (loaded) => !cancelled && setDetail(loaded),
        (caught: unknown) => !cancelled && setError(caught instanceof Error ? caught.message : 'Failed to load the case.'),
      )
      .finally(() => !cancelled && setLoadingCase(false));
    return () => {
      cancelled = true;
    };
  }, [suiteId, caseId]);

  /** Puts a changed case into the panel and the list. */
  const replace = (updated: EvalCaseDetail) => {
    setDetail(updated);
    setCases((current) => {
      const info = withoutVersions(updated);
      const others = current.filter((candidate) => candidate.caseId !== info.caseId);
      return [...others, info].sort((a, b) => a.caseId.localeCompare(b.caseId));
    });
  };

  const counts = useMemo(() => {
    const byStatus: Record<Filter, number> = { all: cases.length, active: 0, proposed: 0, archived: 0 };
    cases.forEach((evalCase) => byStatus[evalCase.status]++);
    return byStatus;
  }, [cases]);

  const openCase = detail && detail.caseId === caseId ? detail : null;
  // The chosen version, else the current one, else the newest.
  const version: EvalCaseVersion | undefined =
    openCase?.versions.find((candidate) => candidate.versionId === searchParams.get('version')) ??
    openCase?.versions.find((candidate) => candidate.versionId === openCase.current?.versionId) ??
    openCase?.versions[0];
  // Archived versions are hidden unless asked for, but the one on view stays.
  const treeVersions = (openCase?.versions ?? []).filter((candidate) => showArchived || !candidate.archived || candidate.versionId === version?.versionId);

  const visible = cases.filter((evalCase) => {
    if (filter !== 'all' && evalCase.status !== filter) return false;
    const words = query.trim().toLowerCase();
    if (!words) return true;
    const current = evalCase.current;
    return [evalCase.caseId, current?.description ?? '', ...(current?.tags ?? [])].some((text) => text.toLowerCase().includes(words));
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <Link to="/evals" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline">
          <ArrowLeft size={14} /> Evals
        </Link>
        <div>
          <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-3xl font-bold text-gray-900">Cases</h1>
              {suiteIds.length > 1 && (
                <select
                  value={suiteId}
                  aria-label="Suite"
                  onChange={(event) => setSearchParams({ suite: event.target.value }, { replace: true })}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-base font-mono text-gray-900 bg-white"
                >
                  {suites.map((suite) => (
                    <option key={suite.id} value={suite.id}>
                      {suite.id} ({suite.promptKey})
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="primary" size="sm" icon={<Plus size={13} />} onClick={() => show({ new: '1' })} disabled={!suiteId}>
                New case
              </Button>
            </div>
          </div>
          <p className="text-sm text-gray-500">
            The cases of <span className="font-mono">{suiteId}</span>, with every version. New runs use the current version of each
            active case; a new version never changes old runs. Cases and versions from Claude have a robot icon and are not used
            until you make them current. Archive a version to reject it: give a reason, and Claude reads it.
          </p>
        </div>

        {error && <ErrorDisplay message={error} />}

        <div className="grid grid-cols-1 lg:grid-cols-[20rem_minmax(0,1fr)] gap-6 items-start">
          <div className="bg-white rounded-lg shadow-sm p-3 space-y-3">
            <label className="relative block">
              <Search size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search ID, description, tags"
                className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-900"
              />
            </label>
            <label className="flex items-center gap-2 text-xs text-gray-700">
              <input type="checkbox" checked={showArchived} onChange={(event) => setShowArchived(event.target.checked)} />
              Show archived versions
            </label>
            <div className="flex flex-wrap gap-1">
              {FILTERS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setFilter(value)}
                  className={`text-xs px-2 py-1 rounded-full border ${filter === value ? 'border-blue-400 bg-blue-50 text-blue-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                >
                  {label} ({counts[value]})
                </button>
              ))}
            </div>
            {loading ? (
              <p className="text-sm text-gray-500">Loading cases…</p>
            ) : (
              <ul className="space-y-1 max-h-[70vh] overflow-y-auto">
                {visible.length === 0 && <li className="text-sm text-gray-500">No cases.</li>}
                {visible.map((evalCase) => {
                  const open = evalCase.caseId === caseId && !creating;
                  return (
                    <li key={evalCase.caseId}>
                      <button
                        type="button"
                        onClick={() => show({ case: evalCase.caseId })}
                        className={`w-full text-left rounded-lg px-2 py-2 ${open ? 'bg-gray-100' : 'hover:bg-gray-50'} ${evalCase.status === 'archived' ? 'opacity-60' : ''}`}
                      >
                        <span className="flex flex-wrap items-center gap-1.5">
                          {open ? <ChevronDown size={14} className="text-gray-500" /> : <ChevronRight size={14} className="text-gray-500" />}
                          <span className="font-mono text-sm font-medium text-gray-900">{evalCase.caseId}</span>
                          <CaseStatusBadge evalCase={evalCase} />
                        </span>
                        {evalCase.current && <span className="block pl-5 text-xs text-gray-600 truncate">{evalCase.current.description}</span>}
                      </button>

                      {open && openCase && (
                        <ul className="ml-4 mt-1 space-y-0.5 border-l border-gray-200 pl-2">
                          {treeVersions.map((entry) => (
                            <li key={entry.versionId}>
                              <button
                                type="button"
                                onClick={() => show({ case: evalCase.caseId, version: entry.versionId })}
                                className={`w-full text-left rounded-md px-2 py-1 text-sm ${
                                  entry.versionId === version?.versionId ? 'bg-blue-50 ring-1 ring-blue-400' : 'hover:bg-gray-50'
                                } ${entry.archived ? 'opacity-60' : ''}`}
                              >
                                <span className="flex items-center gap-1.5">
                                  <span className="font-medium text-gray-900">v{entry.number}</span>
                                  {entry.versionId === openCase.current?.versionId && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-800">current</span>
                                  )}
                                  {entry.archived && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-700">archived</span>}
                                  {entry.tokenName !== undefined && <Bot size={12} className="text-violet-700" aria-label="Proposed by Claude" />}
                                  <span className="ml-auto text-[11px] text-gray-500">{formatDateTime(entry.createdAt)}</span>
                                </span>
                                {entry.note && <span className="block text-xs text-gray-700 truncate">{entry.note}</span>}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {creating ? (
            <div className="bg-white rounded-lg shadow-sm p-4">
              <CaseEditor
                key={suiteId}
                suiteId={suiteId}
                caseOptions={caseOptions}
                onCancel={() => show({})}
                onSave={async ({ caseId: newId, content, note, makeCurrent }) => {
                  const saved = await evalsService.createCaseVersion(suiteId, newId, { content, note, makeCurrent, newCase: true });
                  replace(saved);
                  show({ case: saved.caseId });
                }}
              />
            </div>
          ) : loadingCase && !detail ? (
            <LoadingSpinner message="Loading the case…" />
          ) : openCase && version ? (
            <CasePanel
              key={`${openCase.caseId}~${version.versionId}`}
              evalCase={openCase}
              version={version}
              caseOptions={caseOptions}
              currentUid={user?.uid}
              onCreate={async ({ content, note, makeCurrent }) => {
                const saved = await evalsService.createCaseVersion(suiteId, openCase.caseId, { content, note, makeCurrent, newCase: false });
                replace(saved);
                // Show the version just saved; it is the newest.
                show({ case: saved.caseId, ...(saved.versions[0] && { version: saved.versions[0].versionId }) });
              }}
              onMakeCurrent={async (versionId) => replace(await evalsService.setCurrentCase(suiteId, openCase.caseId, versionId))}
              onSetArchived={async (archived) => replace(await evalsService.setCaseArchived(suiteId, openCase.caseId, archived))}
              onSetVersionArchived={async (versionId, archived, reason) =>
                replace(await evalsService.setCaseVersionArchived(suiteId, openCase.caseId, versionId, archived, reason))
              }
            />
          ) : (
            <p className="text-sm text-gray-500 bg-white rounded-lg shadow-sm p-4">Select a case to see its versions, or make a new one.</p>
          )}
        </div>
      </div>
    </div>
  );
};
